import React, { useState } from 'react';

/**
 * Normalize any YouTube URL to embed format.
 * Supports: /shorts/ID, /watch?v=ID, /embed/ID, youtu.be/ID
 */
export const normalizeYouTubeUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    let videoId = null;

    // youtube.com/shorts/VIDEO_ID
    const shortsMatch = u.pathname.match(/\/shorts\/([a-zA-Z0-9_-]+)/);
    if (shortsMatch) videoId = shortsMatch[1];

    // youtube.com/embed/VIDEO_ID
    if (!videoId) {
      const embedMatch = u.pathname.match(/\/embed\/([a-zA-Z0-9_-]+)/);
      if (embedMatch) videoId = embedMatch[1];
    }

    // youtube.com/watch?v=VIDEO_ID
    if (!videoId && u.searchParams.has('v')) {
      videoId = u.searchParams.get('v');
    }

    // youtu.be/VIDEO_ID
    if (!videoId && (u.hostname === 'youtu.be' || u.hostname === 'www.youtu.be')) {
      const path = u.pathname.replace(/^\//, '');
      if (path) videoId = path.split('/')[0];
    }

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0&controls=1&playsinline=1&enablejsapi=1&fs=0&disablekb=1`;
    }
  } catch {}
  return url; // Return original if not a recognized YouTube URL
};

/**
 * Detect if a YouTube video is vertical (shorts).
 * Returns true if URL was originally from /shorts/ path.
 */
export const isVerticalVideo = (url) => {
  if (!url) return false;
  return /\/shorts\//i.test(url);
};

/**
 * Extract YouTube video ID from any URL format.
 */
export const extractYouTubeId = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const shortsMatch = u.pathname.match(/\/shorts\/([a-zA-Z0-9_-]+)/);
    if (shortsMatch) return shortsMatch[1];
    const embedMatch = u.pathname.match(/\/embed\/([a-zA-Z0-9_-]+)/);
    if (embedMatch) return embedMatch[1];
    if (u.searchParams.has('v')) return u.searchParams.get('v');
    if (u.hostname === 'youtu.be' || u.hostname === 'www.youtu.be') {
      const path = u.pathname.replace(/^\//, '');
      if (path) return path.split('/')[0];
    }
  } catch {}
  return null;
};

/**
 * VideoPlayer component with vertical support, fallback, and skeleton loading.
 */
const VideoPlayer = ({ url, videoType, originalUrl, title = 'Test videosu' }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (!url) return null;

  const isVertical = isVerticalVideo(originalUrl || url);
  const embedUrl = videoType === 'embed' ? normalizeYouTubeUrl(url) : url;
  const videoId = extractYouTubeId(url);
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

  // Outer wrapper: center vertical videos and constrain max-width so bg-black
  // does NOT extend as side bars on wide desktop viewports.
  const outerWrapperClass = isVertical ? 'mx-auto w-full' : 'w-full';
  const outerWrapperStyle = isVertical ? { maxWidth: 420 } : undefined;

  // ── Fallback UI (failed or invalid YouTube URL for embed) ──
  const renderFallback = () => (
    <div className={`${outerWrapperClass} mb-8`} style={outerWrapperStyle}>
      <div
        className="border border-neutral-200 rounded-md bg-neutral-100 overflow-hidden flex items-center justify-center"
        style={isVertical ? { aspectRatio: '9/16' } : { aspectRatio: '16/9' }}
      >
        {thumbnailUrl ? (
          <div className="relative w-full h-full">
            <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <p className="text-white text-sm font-semibold px-4 text-center">Video şu anda görüntülenemiyor.</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-500 font-semibold px-4 text-center">Video şu anda görüntülenemiyor.</p>
        )}
      </div>
    </div>
  );

  if (failed) return renderFallback();

  // Pre-validate YouTube URL for embeds — onError does not fire reliably for
  // cross-origin iframes, so render fallback immediately when no valid id.
  if (videoType === 'embed' && !videoId) return renderFallback();

  if (videoType === 'embed') {
    return (
      <div className={`${outerWrapperClass} mb-8`} style={outerWrapperStyle}>
        <div className="relative overflow-hidden border border-neutral-200 rounded-md bg-black shadow-sm" style={isVertical ? { aspectRatio: '9/16' } : { aspectRatio: '16/9' }}>
          {!loaded && (
            <div className="absolute inset-0 bg-neutral-800 animate-pulse flex items-center justify-center">
              <div className="w-12 h-12 rounded-full border-2 border-neutral-600 border-t-white animate-spin" />
            </div>
          )}
          <iframe
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title={title}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        </div>
      </div>
    );
  }

  // MP4 / direct video
  return (
    <div className={`${outerWrapperClass} mb-8`} style={outerWrapperStyle}>
      <div className="border border-neutral-200 rounded-md bg-black overflow-hidden shadow-sm" style={isVertical ? { aspectRatio: '9/16' } : { aspectRatio: '16/9' }}>
        <video controls preload="none" className="w-full h-full object-contain" playsInline>
          <source src={url} type="video/mp4" />
        </video>
      </div>
    </div>
  );
};

export default VideoPlayer;
